import React, { useCallback, useRef, useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Text,
  Box,
  VStack,
  Checkbox,
  Divider,
  Grid,
  GridItem,
  Input,
  Tooltip,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import Link from "next/link";

import * as N from "../../../../utilities/notifications";
import { getData, patchData } from "../../../../utilities/axios";
import { useDispatch, useSelector } from "react-redux";
import { InfoIcon } from "@chakra-ui/icons";
import { useUtilityDataSelector } from "../../../../utilities/contexts";
import {
  updateProfileValue,
  updateUserDetails,
} from "../../../../redux/actions/user";
import { Button } from "@headlessui/react";
import { FormItemTitle } from "../FormItemTitle";

const OptionalSearch = ({ profile }) => {
  const utilities = useUtilityDataSelector((utilityData) => utilityData);
  const token = useSelector((state) => state.user.JWTToken);
  const initialURL = "https://welldoing.org/counsellors/";
  const dispatch = useDispatch();
  const toast = useToast();

  // PageName
  const [pageName, setPageName] = useState(profile.pageName);
  const {
    isOpen: isPageNameAlertOpen,
    onOpen: onPageNameAlertOpen,
    onClose: onPageNameAlertClose,
  } = useDisclosure();
  const cancelPageNameAlertRef = useRef();
  const allOptInOptions = useUtilityDataSelector(
    (utilityData) => utilityData.optInOptions
  );

  // Set state
  const [optInOptionSelection, setOptInOptionSelection] = useState(
    allOptInOptions.map((optIn) => {
      if (profile.optInOptions && profile.optInOptions.length > 0) {
        const profileOptIns = profile.optInOptions.map((oIO) => oIO.value);
        return {
          ...optIn,
          selected: profileOptIns.includes(optIn.value),
        };
      }

      return optIn;
    })
  );

  const [checkingAvailable, setCheckingAvailable] = useState(false);
  const [availableError, setAvailableError] = useState(false);

  const [profileSelection, setProfileSelection] = useState(null);

  useEffect(() => {
    if (!profile || !utilities) {
      return;
    }

    const profileInsurers = profile.healthInsurers
      ? profile.healthInsurers.map((insurer) => insurer.value)
      : null;
    let healthInsurers = utilities.healthInsurers;
    if (profileInsurers) {
      healthInsurers = healthInsurers.map((insurer) =>
        profileInsurers.includes(insurer.value)
          ? { ...insurer, selected: true }
          : insurer
      );
    }

    const baseSelection = {
      ageGroup: utilities.ageGroups.map((ageGroup) =>
        profile.ageGroup && profile.ageGroup.length > 0
          ? ageGroup.value === profile.ageGroup[0].value
            ? { ...ageGroup, selected: true }
            : ageGroup
          : ageGroup
      ),
      ethnicity: utilities.ethnicities.map((ethnicity) =>
        profile.ethnicity && profile.ethnicity.length > 0
          ? ethnicity.value === profile.ethnicity[0].value
            ? { ...ethnicity, selected: true }
            : ethnicity
          : ethnicity
      ),
      religion: utilities.religions.map((religion) =>
        profile.religion && profile.religion.length > 0
          ? religion.value === profile.religion[0].value
            ? { ...religion, selected: true }
            : religion
          : religion
      ),
      healthInsurers,
      isLGBTQAffirmative: profile.isLGBTQAffirmative ?? false,
      livedLGBTQExperience: profile.livedLGBTQExperience ?? false,
      freeIntroductoryCall: profile.freeIntroductoryCall ?? false,
      isSupervisor: profile.isSupervisor ?? false,
      hiddenSearchValues: profile.hiddenSearchValues || [],
    };

    setProfileSelection(baseSelection);
  }, [profile, utilities]);

  const checkPageName = async () => {
    if (pageName !== profile.pageName && !checkingAvailable) {
      setCheckingAvailable(true);
      try {
        let res = await getData(
          `${process.env.NEXT_PUBLIC_NEW_USER_URL}/availableName/${pageName}`
        );

        if (res.available || res.available === "true") {
          onPageNameAlertOpen();
        } else {
          setAvailableError(true);
        }
      } catch (error) {
        console.error("PROBLEM CHECKING PAGENAME", error);
        toast(N.updateDetailsFail(error));
      }
      setCheckingAvailable(false);
    }
  };

  const updatePageName = useCallback(async () => {
    await patchData(
      token,
      {
        profileId: profile.profileId,
        practitionerType: "therapist",
        pageName,
      },
      `${process.env.NEXT_PUBLIC_NEW_USER_URL}profile`
    );

    await dispatch(
      updateUserDetails({
        therapistProfile: {
          ...profile,
          pageName,
        },
      })
    );
  }, [dispatch, pageName, profile, token]);

  const handleProfileValueUpdate = useCallback(
    (key, value) => {
      dispatch(updateProfileValue(key, value));
    },
    [dispatch]
  );

  // Render
  return (
    <Box position="relative">
      <AlertDialog
        isOpen={isPageNameAlertOpen}
        leastDestructiveRef={cancelPageNameAlertRef}
        onClose={onPageNameAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Changing your personalised URL
            </AlertDialogHeader>

            <AlertDialogBody>
              It looks like you have changed your personalised URL. Is this
              correct?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelPageNameAlertRef}
                onClick={() => {
                  setPageName(profile.pageName);
                  onPageNameAlertClose();
                }}
                variant="ghost"
              >
                Cancel change
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  onPageNameAlertClose();
                  updatePageName();
                }}
                ml={3}
              >
                Yes, update URL
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {/* Title info */}
      <VStack align="flex-start" spacing={4} position="relative" p="30px">
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text textStyle="h3">Personal details</Text>
        </Box>
        <Text
          textStyle="footer2"
          color="brand.200"
          paddingTop="8px"
          textAlign="start"
        >
          Clients often refine their searches to find a practitioner who they
          feel that they can relate to. <br></br>If you would like to appear in
          these search results please fill in your details below.
        </Text>
        <div className="flex flex-col md:grid grid-cols-5 w-full mt-4 gap-4">
          <GridItem>
            <Text textStyle="body2">Age group</Text>
          </GridItem>
          <div className="flex flex-col col-span-4 gap-2 border-b pb-2">
            <div className="flex items-center gap-2">
              {profileSelection?.ageGroup.map((ageGroup) => (
                <Button
                  onClick={() => {
                    setProfileSelection({
                      ...profileSelection,
                      ageGroup: utilities.ageGroups.map((aG) =>
                        aG.value === ageGroup.value
                          ? {
                              ...ageGroup,
                              selected: !ageGroup.selected,
                            }
                          : aG
                      ),
                    });
                    handleProfileValueUpdate(
                      "ageGroup",
                      !ageGroup.selected ? [ageGroup] : null
                    );
                  }}
                  className={`px-4 py-2 ${ageGroup.selected ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                  key={`ageGroup_${ageGroup.value}`}
                >
                  <p
                    className={`${ageGroup.selected ? "text-white" : "text-brand-text"} font-bold transition`}
                  >
                    {ageGroup.label}
                  </p>
                </Button>
              ))}
            </div>
            <div className="flex items-center">
              <Checkbox
                colorScheme="green"
                isChecked={profileSelection?.hiddenSearchValues.includes(
                  "ageGroup"
                )}
                onChange={() => {
                  const removing =
                    profileSelection.hiddenSearchValues.includes("ageGroup");
                  const newArray = removing
                    ? profileSelection.hiddenSearchValues.filter(
                        (item) => item !== "ageGroup"
                      )
                    : [...profileSelection.hiddenSearchValues, "ageGroup"];
                  setProfileSelection({
                    ...profileSelection,
                    hiddenSearchValues: newArray,
                  });

                  handleProfileValueUpdate("hiddenSearchValues", newArray);
                }}
                className="ml-3"
              >
                Hide from public searches
              </Checkbox>
              <FormItemTitle label="This means you are happy for Welldoing to have this information, but you don't want to be searchable by this filter on the public directory. The Welldoing team may use this information to help them service MatchPlus clients who make specific requests." />
            </div>
          </div>
          <GridItem>
            <Text textStyle="body2">Your ethnicity</Text>
          </GridItem>

          <div className="flex flex-col col-span-4 gap-2 border-b pb-2">
            <div className="col-span-4 flex items-center flex-wrap gap-2">
              {profileSelection?.ethnicity.map((ethnicity) => (
                <Button
                  onClick={() => {
                    setProfileSelection({
                      ...profileSelection,
                      ethnicity: utilities.ethnicities.map((eth) =>
                        eth.value === ethnicity.value
                          ? {
                              ...ethnicity,
                              selected: !ethnicity.selected,
                            }
                          : eth
                      ),
                    });
                    handleProfileValueUpdate(
                      "ethnicity",
                      !ethnicity.selected ? [ethnicity] : null
                    );
                  }}
                  className={`px-4 py-2 w-max ${ethnicity.selected ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                  key={`ethnicity_${ethnicity.value}`}
                >
                  <p
                    className={`${ethnicity.selected ? "text-white" : "text-brand-text"} font-bold transition`}
                  >
                    {ethnicity.label}
                  </p>
                </Button>
              ))}
            </div>
            <div className="flex items-center">
              <Checkbox
                colorScheme="green"
                isChecked={profileSelection?.hiddenSearchValues.includes(
                  "ethnicity"
                )}
                onChange={() => {
                  const removing =
                    profileSelection.hiddenSearchValues.includes("ethnicity");
                  const newArray = removing
                    ? profileSelection.hiddenSearchValues.filter(
                        (item) => item !== "ethnicity"
                      )
                    : [...profileSelection.hiddenSearchValues, "ethnicity"];
                  setProfileSelection({
                    ...profileSelection,
                    hiddenSearchValues: newArray,
                  });

                  handleProfileValueUpdate("hiddenSearchValues", newArray);
                }}
                className="ml-3"
              >
                Hide from public searches
              </Checkbox>
              <FormItemTitle label="This means you are happy for Welldoing to have this information, but you don't want to be searchable by this filter on the public directory. The Welldoing team may use this information to help them service MatchPlus clients who make specific requests." />
            </div>
          </div>
          <GridItem>
            <Text textStyle="body2">Religion</Text>
          </GridItem>
          <div className="flex flex-col col-span-4 gap-2 border-b pb-2">
            <div className="col-span-4 flex items-center flex-wrap gap-2">
              {profileSelection?.religion.map((religion) => (
                <Button
                  onClick={() => {
                    setProfileSelection({
                      ...profileSelection,
                      religion: utilities.religions.map((eth) =>
                        eth.value === religion.value
                          ? {
                              ...religion,
                              selected: !religion.selected,
                            }
                          : eth
                      ),
                    });
                    handleProfileValueUpdate(
                      "religion",
                      !religion.selected ? [religion] : null
                    );
                  }}
                  className={`px-4 py-2 w-max ${religion.selected ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                  key={`religion_${religion.value}`}
                >
                  <p
                    className={`${religion.selected ? "text-white" : "text-brand-text"} font-bold transition`}
                  >
                    {religion.label}
                  </p>
                </Button>
              ))}
            </div>
            <div className="flex items-center">
              <Checkbox
                colorScheme="green"
                isChecked={profileSelection?.hiddenSearchValues.includes(
                  "religion"
                )}
                onChange={() => {
                  const removing =
                    profileSelection.hiddenSearchValues.includes("religion");
                  const newArray = removing
                    ? profileSelection.hiddenSearchValues.filter(
                        (item) => item !== "religion"
                      )
                    : [...profileSelection.hiddenSearchValues, "religion"];
                  setProfileSelection({
                    ...profileSelection,
                    hiddenSearchValues: newArray,
                  });

                  handleProfileValueUpdate("hiddenSearchValues", newArray);
                }}
                className="ml-3"
              >
                Hide from public searches
              </Checkbox>
              <FormItemTitle label="This means you are happy for Welldoing to have this information, but you don't want to be searchable by this filter on the public directory. The Welldoing team may use this information to help them service MatchPlus clients who make specific requests." />
            </div>
          </div>
          <GridItem>
            <Text className="mt-2" textStyle="body2">
              LGBTQ+ affirmative
            </Text>
          </GridItem>
          <div className="flex flex-col col-span-4 gap-2 border-b pb-2">
            <div className="col-span-4 flex items-center flex-wrap gap-2">
              <Button
                onClick={() => {
                  setProfileSelection({
                    ...profileSelection,
                    isLGBTQAffirmative: !profileSelection.isLGBTQAffirmative,
                  });
                  handleProfileValueUpdate(
                    "isLGBTQAffirmative",
                    !profileSelection.isLGBTQAffirmative
                  );
                }}
                className={`px-4 py-2 w-max ${profileSelection?.isLGBTQAffirmative ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
              >
                <p
                  className={`${profileSelection?.isLGBTQAffirmative ? "text-white" : "text-brand-text"} font-bold transition`}
                >
                  Yes
                </p>
              </Button>
            </div>
            <div className="flex items-center">
              <Checkbox
                colorScheme="green"
                isChecked={profileSelection?.hiddenSearchValues.includes(
                  "isLGBTQAffirmative"
                )}
                onChange={() => {
                  const removing =
                    profileSelection.hiddenSearchValues.includes(
                      "isLGBTQAffirmative"
                    );
                  const newArray = removing
                    ? profileSelection.hiddenSearchValues.filter(
                        (item) => item !== "isLGBTQAffirmative"
                      )
                    : [
                        ...profileSelection.hiddenSearchValues,
                        "isLGBTQAffirmative",
                      ];
                  setProfileSelection({
                    ...profileSelection,
                    hiddenSearchValues: newArray,
                  });

                  handleProfileValueUpdate("hiddenSearchValues", newArray);
                }}
                className="ml-3"
              >
                Hide from public searches
              </Checkbox>
              <FormItemTitle label="This means you are happy for Welldoing to have this information, but you don't want to be searchable by this filter on the public directory. The Welldoing team may use this information to help them service MatchPlus clients who make specific requests." />
            </div>
          </div>
          <GridItem>
            <Text textStyle="body2">LGBTQ+ lived experience</Text>
          </GridItem>
          <div className="flex flex-col col-span-4 gap-2 border-b pb-2">
            <div className="col-span-4 flex items-center flex-wrap gap-2">
              <Button
                onClick={() => {
                  setProfileSelection({
                    ...profileSelection,
                    livedLGBTQExperience:
                      !profileSelection.livedLGBTQExperience,
                  });
                  handleProfileValueUpdate(
                    "livedLGBTQExperience",
                    !profileSelection.livedLGBTQExperience
                  );
                }}
                className={`px-4 py-2 w-max ${profileSelection?.livedLGBTQExperience ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
              >
                <p
                  className={`${profileSelection?.livedLGBTQExperience ? "text-white" : "text-brand-text"} font-bold transition`}
                >
                  Yes
                </p>
              </Button>
            </div>
            <div className="flex items-center">
              <Checkbox
                colorScheme="green"
                isChecked={profileSelection?.hiddenSearchValues.includes(
                  "livedLGTBQExperience"
                )}
                onChange={() => {
                  const removing = profileSelection.hiddenSearchValues.includes(
                    "livedLGTBQExperience"
                  );
                  const newArray = removing
                    ? profileSelection.hiddenSearchValues.filter(
                        (item) => item !== "livedLGTBQExperience"
                      )
                    : [
                        ...profileSelection.hiddenSearchValues,
                        "livedLGTBQExperience",
                      ];
                  setProfileSelection({
                    ...profileSelection,
                    hiddenSearchValues: newArray,
                  });

                  handleProfileValueUpdate("hiddenSearchValues", newArray);
                }}
                className="ml-3"
              >
                Hide from public searches
              </Checkbox>
              <FormItemTitle label="This means you are happy for Welldoing to have this information, but you don't want to be searchable by this filter on the public directory. The Welldoing team may use this information to help them service MatchPlus clients who make specific requests." />
            </div>
          </div>
          <GridItem colSpan={5}>
            <Text textStyle="h5" className="mt-5 mb-2">
              Practice details
            </Text>
          </GridItem>
          <GridItem>
            <Text textStyle="body2">Free introductory call?</Text>
          </GridItem>
          <div className="col-span-4 flex items-center flex-wrap gap-2">
            <Button
              onClick={() => {
                setProfileSelection({
                  ...profileSelection,
                  freeIntroductoryCall: !profileSelection.freeIntroductoryCall,
                });
                handleProfileValueUpdate(
                  "freeIntroductoryCall",
                  !profileSelection.freeIntroductoryCall
                );
              }}
              className={`px-4 py-2 w-max ${profileSelection?.freeIntroductoryCall ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
            >
              <p
                className={`${profileSelection?.freeIntroductoryCall ? "text-white" : "text-brand-text"} font-bold transition`}
              >
                Yes
              </p>
            </Button>
          </div>
          <GridItem>
            <Text textStyle="body2">
              Do you accept clients paying with the following health insurance?
            </Text>
          </GridItem>

          <div className="col-span-4 flex items-center gap-2 flex-wrap">
            {profileSelection?.healthInsurers.map((healthInsurer) => (
              <Button
                onClick={() => {
                  const insurers = profileSelection.healthInsurers.map(
                    (insurer) =>
                      insurer.value === healthInsurer.value
                        ? {
                            ...healthInsurer,
                            selected: !healthInsurer.selected,
                          }
                        : insurer
                  );
                  setProfileSelection({
                    ...profileSelection,
                    healthInsurers: insurers,
                  });
                  handleProfileValueUpdate(
                    "healthInsurers",
                    insurers.filter((insurer) => insurer.selected)
                  );
                }}
                className={`px-4 py-2 ${healthInsurer.selected ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                key={`healthInsurer_${healthInsurer.value}`}
              >
                <p
                  className={`${healthInsurer.selected ? "text-white" : "text-brand-text"} font-bold transition`}
                >
                  {healthInsurer.label}
                </p>
              </Button>
            ))}
          </div>
        </div>
        <Divider padding="8px" />
        <Text textStyle="h3">Personalised URL</Text>
        <Text
          textStyle="footer2"
          color="brand.200"
          paddingTop="8px"
          textAlign="start"
        >
          Select your own personalised URL here which you can share with your
          clients and your socials.<br></br> Your personal URL is currently{" "}
          <b>
            {initialURL}
            {profile.pageName}
          </b>
        </Text>
        <Grid
          marginTop="16px"
          //h="200px"
          width="100%"
          templateColumns="repeat(5, 1fr)"
          gap={4}
        >
          <GridItem colSpan={5}>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <Text textStyle="body2">{initialURL}</Text>
              <Input
                variant="outline"
                width="300px"
                borderRadius="5px"
                type="text"
                placeholder="Type here..."
                outline={availableError ? "red" : undefined}
                value={pageName}
                onChange={(e) => {
                  setPageName(e.target.value);
                }}
                isDisabled={checkingAvailable}
              />
              {pageName !== profile.pageName && (
                <Button isLoading={checkingAvailable} onClick={checkPageName}>
                  Update page name
                </Button>
              )}
            </Box>
          </GridItem>
        </Grid>
        {availableError && (
          <p className="text-red-500">Page name is unavailable</p>
        )}
        <Divider padding="8px" />
        <Text textStyle="h3">Supervisor</Text>
        <Text
          textStyle="footer2"
          color="brand.200"
          paddingTop="8px"
          textAlign="start"
        >
          Practitioners can search for supervisors. <br></br>If you are a
          qualified supervisor, please indicate below and your profile will be
          returned in the search results.
        </Text>
        <Grid
          marginTop="16px"
          //h="200px"
          width="100%"
          templateColumns="repeat(5, 1fr)"
          gap={4}
        >
          <GridItem>
            <Text textStyle="body2">Are you a qualified supervisor?</Text>
          </GridItem>
          <div className="col-span-4 flex items-center flex-wrap gap-2">
            <Button
              onClick={() => {
                setProfileSelection({
                  ...profileSelection,
                  isSupervisor: !profileSelection.isSupervisor,
                });
                handleProfileValueUpdate(
                  "isSupervisor",
                  !profileSelection.isSupervisor
                );
              }}
              className={`px-4 py-2 w-max ${profileSelection?.isSupervisor ? "bg-brand-alt" : "bg-brand-off"} flex gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
            >
              <p
                className={`${profileSelection?.isSupervisor ? "text-white" : "text-brand-text"} font-bold transition`}
              >
                Yes
              </p>
            </Button>
          </div>
        </Grid>
        <Divider padding="8px" />
        <Text textStyle="h3">Additional lead generation</Text>
        <Text
          textStyle="footer2"
          color="brand.200"
          paddingTop="8px"
          textAlign="start"
        >
          Below are some additional ways that you may be connected with clients
          - please review these and opt in or out
        </Text>
        <Grid
          marginTop="16px"
          templateColumns="repeat(5, 1fr)"
          gap={4}
          width="100%"
          align="left"
        >
          <GridItem>
            <Text textStyle="body2">Current status</Text>
          </GridItem>

          <GridItem colSpan={4}>
            <div className="flex flex-col">
              {optInOptionSelection.map((item) => (
                <div className="flex items-center gap-2" key={item.value}>
                  <Checkbox
                    isChecked={item.selected}
                    colorScheme="green"
                    onChange={(e) => {
                      const optIns = optInOptionSelection.map((optIn) =>
                        optIn.value === item.value
                          ? {
                              ...optIn,
                              selected: e.target.checked,
                            }
                          : optIn
                      );

                      setOptInOptionSelection(optIns);
                      handleProfileValueUpdate(
                        "optInOptions",
                        optIns.filter((optIn) => optIn.selected)
                      );
                    }}
                  >
                    <div className="flex items-center">
                      <p className="px-2">{item.label}</p>
                    </div>
                  </Checkbox>
                  <FormItemTitle label={item.notes} />

                  {item.infoUrl && (
                    <Link href={item.infoUrl} prefetch={false} target="_blank">
                      <span className="text-brand text-sm font-light cursor-pointer hover:underline">
                        Read more
                      </span>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
};

export default OptionalSearch;
